import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useOpticsForDevice } from "@/hooks/useOptic";
import { useCryodeckBottles } from "@/hooks/useCryodeckBottles";
import { useDevice } from "@/hooks/useDevice";
import { qpcrApi, type OpticSummary, type Pathogen as PathogenT } from "@/api/qpcr";
import { cryodeckApi, type CryodeckBottle } from "@/api/cryodeck";

import { Tile, PreviewBanner } from "@/components/common/Tile";
import { Loading } from "@/components/common/Loading";
import { Pathogen } from "@/components/common/Pathogen";

type SortKey = "received_at" | "controls";
type SortDir = "asc" | "desc";


const DAY_MS = 86_400_000;

interface ResultsStats {
  total: number;
  last7d: number;
  uniquePathogens: number;
  lastAt: string | null;
}

function computeStats(optics: OpticSummary[], pathogenMap: Map<number, PathogenT>): ResultsStats {
  const now = Date.now();
  let last7d = 0;
  const pset = new Set<number>();
  let lastAt: string | null = null;
  for (const o of optics) {
    const t = new Date(o.received_at).getTime();
    if (!isNaN(t) && now - t < 7 * DAY_MS) last7d++;
    for (const pid of o.pathogen_ids ?? []) {
      if (pathogenMap.has(pid)) pset.add(pid);
    }
    if (!lastAt || (o.received_at && o.received_at > lastAt)) lastAt = o.received_at;
  }
  return { total: optics.length, last7d, uniquePathogens: pset.size, lastAt };
}

function StatTile({ label, value, hint }: { label: string; value: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-surface p-md flex flex-col gap-xs">
      <div className="text-[10px] uppercase tracking-wider text-textLight font-semibold">{label}</div>
      <div className="text-2xl font-semibold text-text tabular-nums">{value}</div>
      {hint !== undefined && <div className="text-xs text-textLight">{hint}</div>}
    </div>
  );
}

export default function Results() {
  const { deviceId = "" } = useParams();
  const navigate = useNavigate();

  const opticsQ = useOpticsForDevice(deviceId, 100);
  const deviceQ = useDevice(deviceId);
  const pathogensQ = useQuery({
    queryKey: ["pathogens"],
    queryFn: () => qpcrApi.pathogens(),
    staleTime: 5 * 60_000,
  });

  const config = (deviceQ.data?.config ?? {}) as { addons?: string[] };
  const isCryodeck = (config.addons ?? []).includes("cryodeck");
  const unresolvedQ = useCryodeckBottles(deviceId, true);
  const unresolved: CryodeckBottle[] = isCryodeck ? unresolvedQ.data?.bottles ?? [] : [];

  const [sortKey, setSortKey] = useState<SortKey>("received_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [bottleModalOpen, setBottleModalOpen] = useState(false);

  const pathogenMap = useMemo(() => {
    const m = new Map<number, PathogenT>();
    (pathogensQ.data?.pathogens ?? []).forEach((p) => m.set(p.id, p));
    return m;
  }, [pathogensQ.data]);

  const optics: OpticSummary[] = useMemo(
    () => (opticsQ.data?.optics ?? []).filter((o) => !o.hidden),
    [opticsQ.data],
  );

  const stats = useMemo(() => computeStats(optics, pathogenMap), [optics, pathogenMap]);

  const maxTargets = useMemo(
    () => optics.reduce((acc, o) => Math.max(acc, o.pathogen_ids?.length ?? 0), 0),
    [optics],
  );

  const sortedRows = useMemo(() => {
    const rows = [...optics];
    rows.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "received_at") {
        cmp = new Date(a.received_at).getTime() - new Date(b.received_at).getTime();
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [optics, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  const sortArrow = (k: SortKey) =>
    sortKey === k ? (sortDir === "desc" ? " ↓" : " ↑") : "";

  if (opticsQ.isLoading || deviceQ.isLoading) return <Loading />;
  if (opticsQ.error) return <p className="text-danger-text">Could not load results.</p>;

  return (
    <div className="space-y-md">
      <PreviewBanner>
        Quantification values require a backend endpoint upgrade. Showing
        pathogen names only — open a row for the per-target detail.
      </PreviewBanner>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-md">
        <StatTile label="Total tests" value={stats.total} />
        <StatTile label="Last 7 days" value={stats.last7d} />
        <StatTile
          label="Unique pathogens"
          value={stats.uniquePathogens}
          hint={stats.uniquePathogens === 0 ? "—" : `tested on this device`}
        />
        <StatTile
          label="Most recent test"
          value={stats.lastAt ? new Date(stats.lastAt).toLocaleDateString() : "—"}
          hint={stats.lastAt ? new Date(stats.lastAt).toLocaleTimeString() : undefined}
        />
      </div>

      <Tile>
        <div className="flex items-center justify-between mb-md">
          <div>
            {isCryodeck && (
              <button
                onClick={() => setBottleModalOpen(true)}
                disabled={unresolved.length < 1}
                className="rounded-md bg-info-bg text-info-text px-md py-sm text-sm font-medium hover:bg-info-bg/80 disabled:opacity-50 disabled:cursor-not-allowed"
                title={unresolved.length < 1 ? "No cryodeck bottles need labeling" : ""}
              >
                Label Bottles{unresolved.length > 0 ? ` (${unresolved.length})` : ""}
              </button>
            )}
          </div>
          <button
            disabled
            title="Premium / backend"
            className="rounded-md bg-surface border border-border text-textLight px-md py-sm text-sm font-medium cursor-not-allowed opacity-60"
          >
            Export CSV
          </button>
        </div>

        {sortedRows.length === 0 ? (
          <p className="text-textLight text-sm py-md">No results yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-textLight">
                  <th
                    className="py-sm px-sm font-medium cursor-pointer select-none"
                    onClick={() => toggleSort("received_at")}
                  >
                    Time Logged{sortArrow("received_at")}
                  </th>
                  {Array.from({ length: maxTargets }, (_, i) => (
                    <th key={i} className="py-sm px-sm font-medium">
                      Target {i + 1}
                    </th>
                  ))}
                  <th className="py-sm px-sm font-medium text-center">Controls Passed</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/devices/${deviceId}/results/${row.id}`)}
                    className="border-b border-border hover:bg-bg cursor-pointer transition-colors"
                  >
                    <td className="py-sm px-sm tabular-nums">
                      {new Date(row.received_at).toLocaleString()}
                    </td>
                    {Array.from({ length: maxTargets }, (_, i) => {
                      const pid = row.pathogen_ids?.[i];
                      const p = pid !== undefined ? pathogenMap.get(pid) : undefined;
                      return (
                        <td key={i} className="py-sm px-sm">
                          {p ? (
                            <span className="inline-flex items-center rounded-sm bg-purple-bg text-purple-text px-sm py-[2px] text-xs">
                              <Pathogen name={p.name} />
                            </span>
                          ) : pid !== undefined ? (
                            <span className="text-textLight text-xs">#{pid}</span>
                          ) : (
                            <span className="text-textLight">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="py-sm px-sm text-center text-textLight">—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tile>

      {bottleModalOpen && (
        <LabelBottlesModal
          bottles={unresolved}
          onClose={() => setBottleModalOpen(false)}
        />
      )}
    </div>
  );
}

function LabelBottlesModal({
  bottles,
  onClose,
}: {
  bottles: CryodeckBottle[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [labels, setLabels] = useState<Record<number, string>>({});

  const mutation = useMutation({
    mutationFn: async () => {
      const entries = Object.entries(labels).filter(([, v]) => v.trim().length > 0);
      await Promise.all(
        entries.map(([id, location]) =>
          cryodeckApi.setLocation(Number(id), location.trim()),
        ),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["device"], type: "active" });
      onClose();
    },
  });

  const allFilled =
    bottles.length > 0 &&
    bottles.every((b) => (labels[b.id] ?? "").trim().length > 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-md"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-cardHover border border-border p-lg w-full max-w-md max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-text mb-md">
          Label Cryodeck Bottles
        </h2>
        <div className="flex-1 overflow-y-auto space-y-sm">
          {bottles.length === 0 ? (
            <p className="text-textLight text-sm">No bottles need labeling.</p>
          ) : (
            bottles.map((b) => (
              <div key={b.id} className="flex items-center gap-sm">
                <span className="text-sm text-textLight w-24 shrink-0">
                  Bottle #{b.bottle_label_id}:
                </span>
                <input
                  type="text"
                  placeholder="Location label"
                  value={labels[b.id] ?? ""}
                  onChange={(e) =>
                    setLabels((prev) => ({ ...prev, [b.id]: e.target.value }))
                  }
                  className="flex-1 rounded-md border border-border bg-bg px-sm py-xs text-sm focus:outline-none focus:border-info-text"
                />
              </div>
            ))
          )}
        </div>
        <div className="flex justify-end gap-sm mt-md">
          <button
            onClick={onClose}
            className="rounded-md border border-border bg-surface px-md py-sm text-sm font-medium hover:bg-bg"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!allFilled || mutation.isPending}
            className="rounded-md bg-info-bg text-info-text px-md py-sm text-sm font-medium hover:bg-info-bg/80 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
